import React, { useEffect, useState } from "react";
import { AlertTriangle, Link2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { openOAuthTab } from "@/lib/oauth";
import { toast } from "sonner";

/**
 * In-context "Microsoft 365 needs reconnection" banner with a one-tap
 * Reconnect button. Renders ONLY when the user's MS365 token is in the
 * `needs_reconnect` state — so instead of Andre throwing a "365 not connected"
 * error, the user gets an actionable re-authenticate button right where they
 * are (Chat, Inbox).
 *
 * Reconnect hits /integrations/microsoft/connect?reconnect=1, which forces
 * prompt=login so a federated IdP (e.g. Okta) re-authenticates the user
 * end-to-end and Microsoft mints a fresh, clean refresh token.
 */
export const MsReconnectBanner = ({ assistantName = "Andre" }) => {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const refreshStatus = async () => {
    try {
      const r = await api.get("/integrations/microsoft/status");
      setShow(Boolean(r.data?.connected && r.data?.needs_reconnect));
    } catch {
      setShow(false);
    }
  };

  useEffect(() => {
    refreshStatus();
    // Re-check when the user returns to the tab (e.g. after completing the
    // reconnect in the popup) so the banner clears itself.
    const onFocus = () => refreshStatus();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  if (!show) return null;

  const reconnect = async () => {
    setBusy(true);
    const tab = openOAuthTab();
    try {
      const r = await api.get("/integrations/microsoft/connect?reconnect=1");
      tab.go(r.data.authorize_url);
    } catch (e) {
      tab.fail();
      toast.error(e?.response?.data?.detail || "Couldn't start the reconnect. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="ms-reconnect-banner" data-testid="ms-reconnect-banner">
      <AlertTriangle size={16} className="ms-reconnect-icon" aria-hidden="true" />
      <div className="ms-reconnect-copy">
        <strong>Microsoft 365 needs reconnection.</strong>{" "}
        {assistantName} can&rsquo;t read your live calendar or email until you re-authenticate. Your synced data is safe.
      </div>
      <button
        className="btn btn-primary ms-reconnect-btn"
        onClick={reconnect}
        disabled={busy}
        data-testid="ms-reconnect-action"
      >
        {busy ? <Loader2 size={14} className="spin" /> : <Link2 size={14} />} Reconnect
      </button>
    </div>
  );
};

export default MsReconnectBanner;
